import React from 'react';
import styles from './VimanApp.module.css';

// Constants moved outside component to avoid recreation on each render
const USAGE_DATA = [
  {
    icon: 'Assets/Images/Software/usage1.svg',
    text: 'Recreational use of drones'
  },
  {
    icon: 'Assets/Images/Software/usage2.svg',
    text: 'Amateurs and semi professionals operating drones'
  },
  {
    icon: 'Assets/Images/Software/usage3.svg',
    text: 'Professionals and Industrial users handling specialized activities and tasks'
  }
];

const FEATURES_DATA = [
  {
    icon: 'Assets/Images/Software/viman/icon1.svg',
    title: 'Registration and Tracking',
    description: 'Facilitates registration of drones and tracking of flight activities | Proper documentation of project and upcoming schedules'
  },
  {
    icon: 'Assets/Images/Software/viman/icon2.svg',
    title: 'Flight Planning',
    description: 'Define waypoints, altitude, and specific area of interest | Set boundaries to ensure the drone remains within designated areas to avoid restricted zones'
  },
  {
    icon: 'Assets/Images/Software/viman/icon3.svg',
    title: 'Pre-flight Checks',
    description: 'Ensure all necessary pre-flight checks are completed such as battery level, firmware updates & regulatory checks'
  },
  {
    icon: 'Assets/Images/Software/viman/icon4.svg',
    title: 'Data Management',
    description: 'Manage & Organize the data captured by drone | Keep detailed flight logs & history for analysis or regulatory compliance'
  },
  {
    icon: 'Assets/Images/Software/viman/icon5.svg',
    title: 'Data Visualisation and Reports',
    description: 'Provide customizable charts and graphs for visualizing project data & metrics | Generate automated reports based on flight logs and analytics'
  },
  {
    icon: 'Assets/Images/Software/viman/icon6.svg',
    title: 'AI-Powered Weather Integration',
    description: 'Real-time weather data and forecasts | Notification alerts on weather for upcoming projects | AI algorithms that suggest optimal flight times based on predicted weather conditions'
  },
  {
    icon: 'Assets/Images/Software/viman/icon7.svg',
    title: 'Reminders & Notifications',
    description: 'Custom alerts for specific events or changes in weather conditions | Customization for types of notifications user receive | Automated reminders for upcoming projects'
  },
  {
    icon: 'Assets/Images/Software/viman/icon8.svg',
    title: 'Incident Management',
    description: 'Monitor Flight status of each active drone - altitude, speed, distance, time, flight time and location | Monitor flight logs in real-time to ensure the project adheres to regulatory requirements'
  },
  {
    icon: 'Assets/Images/Software/viman/icon9.svg',
    title: 'Historical Data and Reports',
    description: 'Maintain accurate record of each project and operational history with Logs | Precise record of each mission and its activities along with Drones used, Pilot responsible and Operation location'
  },
  {
    icon: 'Assets/Images/Software/viman/icon10.svg',
    title: 'Track & Record',
    description: 'Record information of all Drones, model and UID information with image | Pilot information to map who was responsible for each mission | Pilot license registration to ensure certification, validity and experience'
  },
  {
    icon: 'Assets/Images/Software/viman/icon11.svg',
    title: 'Project Management',
    description: 'Record details of Drone used and Pilot responsible for each Project | Specify the geographic location where the drones operation will take place with the use of GPS coordinates | Upcoming project reminder notifications'
  }
];


// Memoized usage item component
const UsageItem = React.memo(({ icon, text }) => (
  <li className={styles.usageItem}>
    <img src={icon} alt="" className={styles.usageIcon} loading="lazy" />
    <p>{text}</p>
  </li>
));

// Memoized feature item component
const FeatureItem = React.memo(({ icon, title, description }) => (
  // <li className={styles.featureItem}>
  //   <div className={styles.featureIcon}>
  //     <img src={icon} alt={title} className={styles.iconImage} loading="lazy" />
  //   </div>
  //   <div className={styles.featureContent}>
  //     <h4 className={styles.featureTitle}>{title}</h4>
  //     <p className={styles.featureDescription}>{description}</p>
  //   </div>
  // </li>
  <li className={styles.featureItem}>
    <div className={styles.featureItem}>
    <div className={styles.featureIcon}>
      <div className={styles.icon}>
        {typeof icon === 'string' ? (
          <img
            src={icon}
            alt={`${title} icon`}
            className={styles.iconImage}
            width="43"
            height="41"
          />
        ) : (
          icon
        )}
      </div>
    </div>
    <div className={styles.featureContent}>
      <p className={styles.featureTitle}>
        {title}–{' '}
        <span className={styles.featureDescription}>{description}</span>
      </p>
    </div>
  </div>
  </li>
));

const VimanApp = () => {
  const AppDownload = () => {
  return (
    <div className={styles.app_download_container}>
      <a href={"https://apps.apple.com/in/app/axlviman/id6695735424"} target="_blank" rel="noopener noreferrer">
        <img
          src="/Assets/Images/Software/appstore.svg"
          alt="Download on the App Store"
          className={styles.app_store_icon}
        />
      </a>
      <a href={"https://play.google.com/store/apps/details?id=com.actionfi.axlviman"} target="_blank" rel="noopener noreferrer">
        <img
          src="/Assets/Images/Software/playstore.svg"
          alt="Get it on Google Play"
          className={styles.app_store_icon}
        />
      </a>
    </div>
  );
};

  return (
    <div className={styles.vimanContainer}>
      {/* Header Section */}
      <section className={styles.headerSection}>
        <h2 className={styles.mainTitle}>axlviman</h2>
        <p className={styles.subtitle}>Redefining the Flying Experience</p>

        <div className={styles.heroImageSection}>
          <img
            src="Assets/Images/Software/Viman.svg"
            alt="Viman mobile app interface"
            className={styles.heroImage}
            loading="lazy"
          />
        </div>
      </section>

      {/* Description Section */}
      <section className={styles.descriptionSection}>
        <p>
          axlviman is a powerful mobile application designed to simplify and enhance drone operations. Built to elevate the user experience, it offers a comprehensive suite of features for planning, managing, and executing drone missions—whether you're a beginner or a seasoned operator. With integrated project tracking, flight planning tools, and real-time access to DGCA regulations and compliance guidelines, axl Viman ensures safe, efficient, and fully compliant operations every time you take off.
        </p>
        <AppDownload />
      </section>

      {/* Workflow Section */}
      <section className={styles.workflowSection}>
        <h3>A comprehensive app that helps to track the drone, Flight activities, Log and Compliance</h3>

        <div className={styles.workflowImageSection}>
          <img
            src="Assets/Images/Software/workflow.svg"
            alt="Viman app workflow diagram"
            className={styles.workflowImage}
            loading="lazy"
          />
        </div>
      </section>

      {/* Usage Section */}
      <section className={styles.usageSection}>
        <h3>Who is it for?</h3>

        <div className={styles.usageContainer}>
          <ul className={styles.usageList}>
            {USAGE_DATA.map((item, index) => (
              <UsageItem
                key={index}
                icon={item.icon}
                text={item.text}
              />
            ))}
          </ul>
        </div>
      </section>

      {/* App Image Section */}
      <section className={styles.appImageSection}>
        <img
          src="Assets/Images/Software/Viman2.svg"
          alt="Viman mobile app screenshots"
          className={styles.appImage}
          loading="lazy"
        />
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <h3>What does it offer?</h3>

        <ul className={styles.featuresList}>
          {FEATURES_DATA.map((item, index) => (
            <FeatureItem
              key={index}
              icon={item.icon}
              title={item.title}
              description={item.description}
            />
          ))}
        </ul>
      </section>
    </div>
  );
};

export default VimanApp;